﻿import { request, ApiRequestError } from "./client";
import { API_URL, authHeaders, ORGANISATION_BASE } from "./config";
import { silentRefresh } from "@/api/refresh";
import { getUploadTicket, putUpload } from "@/api/upload";
import { fetchBusinessUnits } from "@/features/facilities/api/businessUnitService";
import { getProtocol } from "@/lib/protocol";
import { authIdentity } from "@/utils/token";
import { canRefreshAuthentication } from "./authFailure";
import { tokenStore } from "@/auth/tokenStore";
import { queueIntakePreview, type IntakePreview } from "./intakePreviewQueue";
import type {
  ChatConversation,
  FileAttachment,
  BusinessUnit,
  ReviewResponse,
  ConfirmResponse,
  Approval,
  ConversationalEmissionReview,
  ConversationalEmissionEdits,
  EmissionCalculationPreview,
  ScreenContext,
} from "../types";

interface ChatRequest {
  message: string;
  requestId?: string;
  chatId?: string;
  businessUnitId?: string;
  businessUnitName?: string;
  logEmissionEnabled?: boolean;
  files?: Pick<FileAttachment, "fileName" | "s3Url">[];
  pageContext?: {
    page: string;
    filters?: Record<string, unknown>;
    screenContext?: ScreenContext;
  };
  formContext?: import("@/types").FormContext;
}

interface ChatResponse {
  response: string;
  chatId: string;
  isReport?: boolean;
  autoDownload?: { format: "pdf" | "docx" } | null;
  pendingCreateId?: number;
}

const auth = authHeaders;

export const sendMessage = async (
  data: ChatRequest,
  token: string,
): Promise<ChatResponse> => {
  const requestData = {
    ...data,
    requestId: data.requestId ?? crypto.randomUUID(),
  };
  try {
    const protocol = getProtocol();
    const body = await request<{
      message: string;
      chat_id: string;
      is_report?: boolean;
      auto_download?: ChatResponse["autoDownload"];
      pending_create_id?: number | null;
    }>("post", `${API_URL}/api/conversation?protocol=${protocol}`, {
      data: requestData,
      ...auth(token),
      timeout: 130_000,
    });
    return {
      response: body.message,
      chatId: String(body.chat_id),
      isReport: !!body.is_report,
      autoDownload: body.auto_download || null,
      pendingCreateId: body.pending_create_id ?? undefined,
    };
  } catch (error) {
    throw new Error(`Failed to send message: ${(error as Error).message}`, {
      cause: error,
    });
  }
};

interface StreamCallbacks {
  onAccepted?: (chatId: string, requestId: string) => void;
  onToolStep?: (label: string, status: "running" | "done" | "error") => void;
  onProgress?: (message: string) => void;
  onToken?: (text: string) => void;
  onResult?: (
    text: string,
    chatId: string,
    isReport: boolean,
    autoDownload?: unknown,
    pendingCreateId?: number,
  ) => void;
  onError?: (message: string) => void;
  onFileBubble?: (files: FileAttachment[]) => void;
  onReviewReady?: (batchId: string, recordCount: number) => void;
  onUploadSubmitted?: (jobId: number, chatId: string) => void;
}

export const sendMessageStreaming = async (
  data: ChatRequest,
  token: string,
  callbacks: StreamCallbacks,
  signal?: AbortSignal,
): Promise<void> => {
  const requestData = {
    ...data,
    requestId: data.requestId ?? crypto.randomUUID(),
  };
  let accepted = false;
  let terminal = false;
  const recover = async () => {
    if (
      signal?.aborted ||
      authIdentity(token) !== authIdentity(tokenStore.get())
    )
      return;
    const turn = await request<{
      chatId: number;
      status: string;
      result?: {
        result: string;
        chat_id: number;
        isReport: boolean;
        autoDownload?: unknown;
        pendingCreateId?: number;
        reviewBatch?: { batchId: string; recordCount: number };
      };
    }>(
      "get",
      `${API_URL}/api/conversation/turns/${requestData.requestId}`,
      auth(token),
    );
    if (
      signal?.aborted ||
      authIdentity(token) !== authIdentity(tokenStore.get())
    )
      return;
    if (!accepted)
      callbacks.onAccepted?.(String(turn.chatId), requestData.requestId);
    accepted = true;
    if (turn.result && !terminal) {
      terminal = true;
      if (turn.result.reviewBatch)
        callbacks.onReviewReady?.(
          turn.result.reviewBatch.batchId,
          turn.result.reviewBatch.recordCount,
        );
      callbacks.onResult?.(
        turn.result.result,
        String(turn.chatId),
        turn.result.isReport,
        turn.result.autoDownload,
        turn.result.pendingCreateId,
      );
    }
  };
  try {
    const protocol = getProtocol();
    const perform = (accessToken: string): Promise<Response> =>
      fetch(`${API_URL}/api/conversation/stream?protocol=${protocol}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(accessToken).headers,
        },
        body: JSON.stringify(requestData),
        signal,
      });

    let response = await perform(token);

    if (
      response.status === 401 &&
      canRefreshAuthentication(
        await response
          .clone()
          .json()
          .catch(() => null),
      )
    ) {
      if (authIdentity(token) !== authIdentity(tokenStore.get())) {
        throw new Error("The authenticated session changed before refresh.");
      }
      const fresh = await silentRefresh();
      if (fresh) {
        if (authIdentity(token) !== authIdentity(fresh)) {
          throw new Error(
            "The authenticated session changed before the message could be retried.",
          );
        }
        response = await perform(fresh);
      }
    }

    if (!response.ok) {
      const errBody = await response.text().catch(() => "");
      if (
        response.status === 401 &&
        authIdentity(token) === authIdentity(tokenStore.get())
      ) {
        window.dispatchEvent(new Event("auth:session-expired"));
      }
      throw new Error(
        `Server error: ${response.status}${errBody ? ` - ${errBody}` : ""}`,
      );
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        try {
          const event = JSON.parse(line.slice(6));
          switch (event.type) {
            case "accepted":
              accepted = true;
              callbacks.onAccepted?.(
                String(event.chatId),
                requestData.requestId,
              );
              break;
            case "tool_step":
              callbacks.onToolStep?.(event.label, event.status);
              break;
            case "progress":
              callbacks.onProgress?.(event.message);
              break;
            case "token":
              callbacks.onToken?.(event.text);
              break;
            case "result":
              terminal = true;
              if (event.reviewBatch)
                callbacks.onReviewReady?.(
                  event.reviewBatch.batchId,
                  event.reviewBatch.recordCount,
                );
              callbacks.onResult?.(
                event.text,
                String(event.chatId),
                event.isReport,
                event.autoDownload,
                event.pendingCreateId,
              );
              break;
            case "error":
              terminal = true;
              callbacks.onError?.(
                event.referenceId
                  ? `${event.message} (Reference: ${event.referenceId})`
                  : event.message,
              );
              break;
            case "file_bubble":
              callbacks.onFileBubble?.(event.files);
              break;
            case "upload_job":
              callbacks.onUploadSubmitted?.(event.jobId, String(event.chatId));
              break;
            case "review_ready":
              callbacks.onReviewReady?.(event.batchId, event.recordCount);
              break;
          }
        } catch {
          /* skip malformed JSON */
        }
      }
    }
    if (!terminal) await recover().catch(() => undefined);
  } catch (error) {
    await recover().catch(() => undefined);
    if (!terminal) throw error;
  }
};

export const getChats = async (
  userId: string,
  token: string,
  limit: number = 20,
  offset: number = 0,
): Promise<{ chats: ChatConversation[]; total: number }> => {
  try {
    const body = await request<{
      data?: { chats?: ChatConversation[] };
      meta?: { pagination?: { total?: number } };
    }>("get", `${API_URL}/api/chats`, {
      params: { userId, limit, offset },
      ...auth(token),
      unwrap: false,
    });
    return {
      chats: body.data?.chats ?? [],
      total: body.meta?.pagination?.total ?? 0,
    };
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 401) throw error;
    throw new Error(`Failed to load chats: ${(error as Error).message}`, {
      cause: error,
    });
  }
};

export const getChatMessages = async (chatId: string, token: string) => {
  try {
    const body = await request<{
      data?: {
        chat_id: string;
        messages: unknown[];
        follow_up_questions: string[];
      };
    }>("get", `${API_URL}/api/chats/${chatId}/messages`, {
      ...auth(token),
      unwrap: false,
    });
    return body.data;
  } catch (error) {
    throw new Error(`Failed to fetch messages: ${(error as Error).message}`, {
      cause: error,
    });
  }
};

export const deleteChat = (chatId: string, token: string): Promise<void> =>
  request("delete", `${API_URL}/api/chats/${chatId}`, auth(token));

export const generateChatTitle = async (
  chatId: string,
  message: string,
  token: string,
): Promise<void> => {
  try {
    await request("post", `${API_URL}/api/chats/${chatId}/generate-title`, {
      data: { message },
      ...auth(token),
    });
  } catch {
    // Non-blocking enhancement; ignore failures.
  }
};

export const getIntakeReview = (
  chatId: string,
  batchId: string,
  token: string,
): Promise<ReviewResponse> =>
  request("get", `${API_URL}/api/emissions/intake/review`, {
    params: { chatId, batchId },
    ...auth(token),
  });

export const confirmIntake = (
  chatId: string,
  batchId: string,
  approvals: Approval[],
  token: string,
  coverageReason?: string,
): Promise<ConfirmResponse> =>
  request("post", `${API_URL}/api/emissions/intake/confirm`, {
    data: {
      chatId,
      batchId,
      approvals,
      ...(coverageReason ? { coverageReason } : {}),
    },
    ...auth(token),
    timeout: 130_000,
  });

export const previewIntakeRecord = (
  chatId: string,
  batchId: string,
  recordId: number,
  edits: Record<string, string | number>,
  token: string,
  signal?: AbortSignal,
): Promise<IntakePreview> =>
  queueIntakePreview({ chatId, batchId, recordId, edits, token, signal });

export const reconcileIntake = (
  chatId: string,
  batchId: string,
  token: string,
): Promise<ReviewResponse> =>
  request("post", `${API_URL}/api/emissions/intake/reconcile`, {
    data: { chatId, batchId },
    ...auth(token),
    timeout: 130_000,
  });

export const getIntakeSource = (
  chatId: string,
  batchId: string,
  fileId: string,
  token: string,
): Promise<Blob> =>
  request("get", `${API_URL}/api/emissions/intake/source`, {
    params: { chatId, batchId, fileId },
    responseType: "blob",
    unwrap: false,
    ...auth(token),
  });

export const rejectIntake = (
  chatId: string,
  batchId: string,
  token: string,
): Promise<void> =>
  request("post", `${API_URL}/api/emissions/intake/reject`, {
    data: { chatId, batchId },
    ...auth(token),
  });

export const getConversationalEmissionReview = (
  chatId: string,
  messageId: number,
  token: string,
  context: Partial<ConversationalEmissionEdits> = {},
) =>
  request<ConversationalEmissionReview>(
    "get",
    `${API_URL}/api/emissions/intake/conversation/review`,
    {
      params: { chatId, messageId, ...context },
      ...auth(token),
    },
  );

export const previewConversationalEmission = (
  chatId: string,
  messageId: number,
  edits: ConversationalEmissionEdits,
  token: string,
) =>
  request<{
    failed: number;
    errors: Array<{ message: string }>;
    emissions: Array<{
      sourceRow: number;
      calculation: EmissionCalculationPreview;
    }>;
  }>("post", `${API_URL}/api/emissions/intake/conversation/preview`, {
    data: { chatId, messageId, ...edits },
    ...auth(token),
  });

export const confirmConversationalEmission = (
  chatId: string,
  messageId: number,
  edits: ConversationalEmissionEdits,
  token: string,
) =>
  request<ConfirmResponse>(
    "post",
    `${API_URL}/api/emissions/intake/conversation/confirm`,
    {
      data: { chatId, messageId, ...edits },
      ...auth(token),
    },
  );

export const rejectConversationalEmission = (
  chatId: string,
  messageId: number,
  token: string,
) =>
  request("post", `${API_URL}/api/emissions/intake/conversation/reject`, {
    data: { chatId, messageId },
    ...auth(token),
  });

type ReportFormat = "pdf" | "docx";

export const uploadFileToGateway = async (
  file: File,
  token: string,
): Promise<string> => {
  const ticket = await getUploadTicket(file, "evidence", token);
  try {
    await putUpload(ticket, file);
  } catch (error) {
    // A lost S3 response can still mean the object was committed. Remove the exact
    // ticketed object before returning the transfer error to the chat composer.
    await cleanupFile(ticket.s3Url, token).catch(() => {});
    throw error;
  }
  return ticket.s3Url;
};

export const cleanupFile = (s3Url: string, token: string): Promise<void> =>
  request("post", `${ORGANISATION_BASE}/upload-cleanup`, {
    data: { s3Url },
    ...auth(token),
  });

export const getBusinessUnits = async (
  token: string,
): Promise<BusinessUnit[]> => {
  try {
    const { businessUnits } = await fetchBusinessUnits(undefined, token);
    return businessUnits;
  } catch (error) {
    console.warn("Failed to load business units:", error);
    return [];
  }
};

export const downloadReport = (
  content: string,
  title: string,
  token: string,
  format: ReportFormat = "pdf",
): Promise<Blob> =>
  request("post", `${API_URL}/api/reports/download-${format}`, {
    data: { content, title },
    ...auth(token),
    responseType: "blob",
    unwrap: false,
  });
